import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { TokenService } from 'src/app/shared/services/token.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { GetterSetterService } from '../../../shared/services/getter-setter.service';
import { BaseResponse } from '../../../models/response';

@Component({
  selector: 'app-sovos-callback',
  templateUrl: './sovos-callback.component.html',
  styleUrls: ['./sovos-callback.component.scss']
})
export class SovosCallbackComponent implements OnInit {
  displayConsentModal = false;
  isChecked = false;
  isSubmittedConsent = false;
  isProcessing = true;
  consentEmail = '';

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private tokenService: TokenService,
    private toaster: ToasterService,
    private getSet: GetterSetterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const code = params['code'];
      const state = params['state'];

      if (this.authService.isAuthenticated()) {
        const companyId = this.getSet.getSetCompanyId();
        if (companyId) {
          this.router.navigate(['dashboard']);
        } else {
          this.router.navigate(['add-business']);
        }
        return;
      }

      if (!code || !state) {
        // this.toaster.showError('Invalid callback parameters.');
        this.router.navigate(['login']);
        return;
      }

      this.authService.processSovosCallbackGet(code, state).subscribe({
        next: (resp: BaseResponse) => this.handleCallbackResponse(resp),
        error: (error) => {
          this.isProcessing = false;
          const logoutUrl = error?.error?.response?.sovosLogoutUrl;
          if (logoutUrl) {
            this.redirectToSovosLogout(logoutUrl);
            return;
          }
          console.error('Sovos callback GET processing error:', error);
          const errorMessage = error?.error?.message || error?.message;
          this.toaster.showError(
            errorMessage
              ? `There was an error processing login request: ${errorMessage}`
              : 'There was an error processing login request.'
          );
        }
      });
    });
  }

  private handleCallbackResponse(resp: BaseResponse): void {
    this.isProcessing = false;

    if (!resp) {
      this.toaster.showError('Sovos authentication failed.');
      this.router.navigate(['login']);
      return;
    }

    try {
      if (resp.status === 'SUCCESS') {
        if (resp.response?.consentGiven === false) {
          this.consentEmail = resp.response.email;
          this.showConsentModal();
          return;
        }
        this.proceedWithLogin(resp);
        return;
      }

      if (resp.response?.sovosLogoutUrl) {
        this.redirectToSovosLogout(resp.response.sovosLogoutUrl);
        return;
      }

      if (resp.response != null && resp.response.isValidPassword === false) {
        this.toaster.showError(
          'Your password has expired and must be changed after redirecting.'
        );
        this.authService.setLoginEmail(resp.response.email);
        this.router.navigate(['change-password']);
      }
      this.toaster.showError(resp.message);
    } catch (e) {
      this.toaster.showError('Login succeeded but failed to initialize session.');
      this.router.navigate(['login']);
    }
  }

  showConsentModal(): void {
    this.displayConsentModal = true;
    this.isChecked = false;
    this.isSubmittedConsent = false;
  }

  submitConsent(): void {
    this.isSubmittedConsent = true;
    if (!this.isChecked) {
      return;
    }

    if (!this.consentEmail) {
      this.toaster.showError('Unable to submit consent. Please try logging in again.');
      return;
    }

    this.isProcessing = true;
    this.authService.submitSovosConsent(this.consentEmail).subscribe({
      next: (resp: BaseResponse) => {
        this.isProcessing = false;
        if (resp?.status === 'SUCCESS') {
          this.displayConsentModal = false;
          this.router.navigate(['sovos_login']);
        } else {
          this.toaster.showError(resp?.message || 'Failed to record consent.');
        }
      },
      error: () => {
        this.isProcessing = false;
      }
    });
  }

  private redirectToSovosLogout(logoutUrl: string): void {
    this.tokenService.destroy();
    this.authService.setLoginIndicator(0);
    window.location.replace(logoutUrl);
  }

  private proceedWithLogin(resp: BaseResponse): void {
    if (resp.response?.mfa_enable) {
      this.getSet.getSetUserId(resp.response.email);
      this.authService.setLoginIndicator(0);
      this.getSet.setMFACode(resp.response.mfa_enable);
      this.router.navigate(['verify-otp']);
      return;
    }

    this.toaster.showSuccess('Login Successful.');
    this.tokenService.setter(resp.response);
    this.authService.setLoginIndicator(2);
    this.getSet.getSetUser(resp.response.username);
    this.getSet.getSetSapphireTenent(resp.response.sapphireTenant);
    this.getSet.getSetUserId(resp.response.email);

    if (resp.response.rootCompanyid != undefined) {
      this.getSet.getSetCompanyId(resp.response.rootCompanyid);
      this.getSet.getSetCompany(resp.response.rootCompanyName);
      if (resp.response.products && resp.response.products.length > 0) {
        this.getSet.setProduct(resp.response.products);
      }
      this.router.navigate(['dashboard']);
    } else {
      this.router.navigate(['add-business']);
    }
  }
}
